import React, { useState, useEffect } from "react";
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button, 
  Divider, 
  Fade,
  Tooltip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const UserVip = () => {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  
  useEffect(() => {
    setLoaded(true);
  }, []);

  const features = [
    {
      id: 'download-instructions',
      title: 'הסבר להורדת יתרות לסילוק',
      description: 'קבל הסבר מפורט כיצד להוריד את דוח היתרות לסילוק, שלב אחר שלב.',
      icon: <DescriptionOutlinedIcon />,
      action: () => navigate("/how-to-download"),
      actionText: 'קרא עוד',
      available: true
    },
    {
      id: 'refinance-check',
      title: 'בדיקת מחזור ללא עלות',
      description: 'ניתוח מחזורי ההלוואה והמלצות לשיפור התשלומים – ללא עלות. אנו נחזור אליכם תוך יום עסקים.',
      icon: <CachedOutlinedIcon />,
      action: () => navigate("/UploadPage"),
      actionText: 'התחל עכשיו',
      available: true,
      highlight: true
    },
    {
      id: 'mortgage-tracking',
      title: 'מעקב אחר המשכנתא שלך',
      description: 'מעקב בזמן אמת אחרי תנאי המשכנתא והמלצות להתייעלות.',
      icon: <TimelineOutlinedIcon />,
      actionText: 'בקרוב...',
      available: false
    },
    {
      id: 'ai-consultant',
      title: 'יועצת משכנתאות AI',
      description: 'התייעץ עם יועצת משכנתאות מבוססת AI לקבלת המלצות מותאמות אישית.',
      icon: <SmartToyOutlinedIcon />,
      actionText: 'בקרוב...',
      available: false
    },
    {
      id: 'interest-rates',
      title: 'ריביות ממוצעות',
      description: 'גלה את הריביות הממוצעות בשוק והבנקים השונים.',
      icon: <PercentOutlinedIcon />,
      actionText: 'בקרוב...',
      available: false
    },
    {
      id: 'early-payment',
      title: 'חישוב עמלת פירעון מוקדם',
      description: 'מחשבון מתקדם לחישוב העמלה בעת פירעון מוקדם של המשכנתא.',
      icon: <CalculateOutlinedIcon />,
      actionText: 'בקרוב...',
      available: false
    },
    {
      id: 'forecast-updates',
      title: 'עוגנים ותחזיות עדכון',
      description: 'נתוני עוגנים ותחזיות עדכון עוגנים בבנקים השונים.',
      icon: <InsightsOutlinedIcon />,
      actionText: 'בקרוב...',
      available: false
    },
    {
      id: 'free-consultation',
      title: 'קביעת פגישה לייעוץ חינם',
      description: 'קבע פגישה עם יועץ משכנתאות מקצועי ללא התחייבות.',
      icon: <EventAvailableOutlinedIcon />,
      actionText: 'בקרוב...',
      available: false
    }
  ];

  return (
    <Fade in={loaded} timeout={800}>
      <VipContainer>
        {/* Premium Header with Badge */}
        <PremiumBadge>
          <WorkspacePremiumIcon sx={{ fontSize: 22, color: "#C9B18C" }} />
          <Typography variant="overline" sx={{ letterSpacing: 1.5, mx: 0.5 }}>
            Exclusive
          </Typography>
        </PremiumBadge>
        
        <PremiumHeader>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 700, 
              color: "#2E353E",
              mb: 1
            }}
          >
            שירותי <VipText>VIP</VipText>
          </Typography>
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: "#5E6B7D",
              maxWidth: "600px",
              mx: "auto",
              lineHeight: 1.6
            }}
          >
            שירותים ייחודיים ואקסקלוסיביים לחברים בלבד, המעניקים לך יתרון משמעותי בניהול המשכנתא
      </Typography>
        </PremiumHeader>
        
        <Divider sx={{ width: "80px", margin: "24px auto", borderColor: "#C9B18C", borderBottomWidth: 2 }} />
        
        <VipExplanation>
          <LockIconWrapper>
            <LockIcon sx={{ fontSize: 18, color: "#C9B18C" }} />
          </LockIconWrapper>
          <Typography variant="body2" sx={{ color: "#5E6B7D" }}>
            הגישה לשירותים אלו זמינה רק לחברי VIP מאומתים במערכת
      </Typography>
        </VipExplanation>

        <FeaturesGrid container spacing={3}>
          {features.map((feature, index) => (
            <Fade 
              in={loaded} 
              style={{ transitionDelay: `${150 * index}ms` }} 
              timeout={800} 
              key={feature.id}
            >
              <Grid item xs={12} sm={6} md={4}>
                <FeatureCard 
                  available={feature.available}
                  highlight={feature.highlight}
                >
                  <StatusDot available={feature.available} />
                  
                  <IconWrapper available={feature.available}>
                    {feature.icon}
                  </IconWrapper>
                  
                  <CardContent sx={{ pt: 0, height: "100%", display: "flex", flexDirection: "column" }}>
                    <FeatureTitle>
                      {feature.title}
                      {feature.available && (
                        <Tooltip title="שירות מאומת" placement="top">
                          <VerifiedBadge>
                            <VerifiedUserIcon fontSize="small" />
                          </VerifiedBadge>
                        </Tooltip>
                      )}
                    </FeatureTitle>
                    
                    <FeatureDescription>
                      {feature.description}
                    </FeatureDescription>
                    
                    {feature.available ? (
                      <FeatureButton 
                        variant="outlined"
                        onClick={feature.action}
                        highlight={feature.highlight}
                      >
                        {feature.actionText}
                      </FeatureButton>
                    ) : (
                      <ComingSoonButton variant="outlined">
                        {feature.actionText}
                      </ComingSoonButton>
                    )}
            </CardContent>
                </FeatureCard>
        </Grid>
            </Fade>
          ))}
        </FeaturesGrid>
        
        <VipAdvantages>
          <AdvantageTitle>
            <WorkspacePremiumIcon sx={{ fontSize: 18, color: "#C9B18C", mr: 1 }} />
            היתרונות שלך כחבר VIP
          </AdvantageTitle>
          
          <AdvantagesList>
            <AdvantageItem>
              <AdvantageIcon />
              <Typography variant="body2">גישה בלעדית לכלים מתקדמים</Typography>
            </AdvantageItem>
            <AdvantageItem>
              <AdvantageIcon />
              <Typography variant="body2">תמיכה אישית ממומחי משכנתאות</Typography>
            </AdvantageItem>
            <AdvantageItem>
              <AdvantageIcon />
              <Typography variant="body2">מידע מקדים על עדכוני ריבית</Typography>
            </AdvantageItem>
            <AdvantageItem>
              <AdvantageIcon />
              <Typography variant="body2">הטבות ייחודיות ממוסדות פיננסיים</Typography>
            </AdvantageItem>
          </AdvantagesList>
        </VipAdvantages>
        
        <VipFooter>
          <Typography variant="body2" sx={{ color: "#5E6B7D", fontSize: "0.85rem", letterSpacing: 0.2 }}>
            שירותי VIP &copy; {new Date().getFullYear()} | חוויה מותאמת אישית למשכנתא שלך
          </Typography>
        </VipFooter>
      </VipContainer>
    </Fade>
  );
};

export default UserVip;

// Styled Components
const VipContainer = styled(Box)`
  max-width: 1200px;
  margin: 40px auto;
  padding: 40px 30px;
  direction: rtl;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    height: 5px;
    width: 100%;
    background: linear-gradient(to right, #C9B18C, #E9DBC8);
  }
`;

const PremiumBadge = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #FBFAF8;
  border: 1px solid #F0EBE4;
  border-radius: 20px;
  padding: 4px 12px;
  width: fit-content;
  margin: 0 auto 16px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.03);
`;

const PremiumHeader = styled(Box)`
  text-align: center;
  padding: 10px 0 20px;
`;

const VipText = styled.span`
  position: relative;
  color: #2E353E;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background-color: rgba(201, 177, 140, 0.3);
    z-index: -1;
  }
`;

const VipExplanation = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 5px auto 30px;
  gap: 8px;
`;

const LockIconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #F8F5F0;
  border-radius: 50%;
  width: 24px;
  height: 24px;
`;

const FeaturesGrid = styled(Grid)`
  margin-top: 20px;
  margin-bottom: 40px;
`;

const FeatureCard = styled(Card)`
  height: 100%;
  position: relative;
  overflow: visible;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: ${props => props.highlight 
    ? '0 12px 28px rgba(201, 177, 140, 0.15)' 
    : '0 8px 24px rgba(0, 0, 0, 0.04)'};
  transition: all 0.4s ease;
  border: 1px solid ${props => props.highlight 
    ? 'rgba(201, 177, 140, 0.3)' 
    : '#f0f0f0'};
  display: flex;
  flex-direction: column;
  padding-top: 22px;
  
  &:hover {
    transform: translateY(-6px);
    box-shadow: ${props => props.available 
      ? (props.highlight 
        ? '0 16px 32px rgba(201, 177, 140, 0.2)' 
        : '0 14px 30px rgba(0, 0, 0, 0.08)') 
      : '0 10px 25px rgba(0, 0, 0, 0.06)'};
  }
  
  ${props => props.highlight && `
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      right: 24px;
      left: 24px;
      height: 6px;
      border-radius: 3px 3px 0 0;
      background: linear-gradient(to right, #C9B18C, #E9DBC8);
    }
  `}
`;

const StatusDot = styled.div`
  position: absolute;
  top: 18px;
  right: 18px;
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background-color: ${props => props.available ? '#4CAF50' : '#BDBDBD'};
  box-shadow: 0 0 0 3px ${props => props.available ? 'rgba(76, 175, 80, 0.15)' : 'rgba(189, 189, 189, 0.15)'};
`;

const IconWrapper = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => props.available ? 'linear-gradient(135deg, #F8F5F0, #E9DBC8)' : '#F5F5F5'};
  color: ${props => props.available ? '#896E3B' : '#9E9E9E'};
  box-shadow: 0 6px 12px ${props => props.available ? 'rgba(201, 177, 140, 0.2)' : 'rgba(0, 0, 0, 0.04)'};
`;

const FeatureTitle = styled(Typography)`
  font-weight: 600;
  font-size: 1.125rem;
  margin-bottom: 12px;
  color: #2E353E;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const VerifiedBadge = styled(Box)`
  display: flex;
  color: #C9B18C;
  font-size: 16px;
  cursor: help;
`;

const FeatureDescription = styled(Typography)`
  color: #5E6B7D;
  margin-bottom: 24px;
  flex-grow: 1;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const FeatureButton = styled(Button)`
  border-color: ${props => props.highlight ? '#C9B18C' : '#616E7C'};
  color: ${props => props.highlight ? '#896E3B' : '#616E7C'};
  border-radius: 8px;
  padding: 10px 0;
  font-weight: 500;
  text-transform: none;
  border-width: ${props => props.highlight ? '1.5px' : '1px'};
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.highlight ? 'rgba(201, 177, 140, 0.08)' : 'rgba(97, 110, 124, 0.05)'};
    border-color: ${props => props.highlight ? '#B3986D' : '#505A66'};
  }
`;

const ComingSoonButton = styled(Button)`
  border-color: #E0E0E0;
  color: #9E9E9E;
  cursor: not-allowed;
  border-radius: 8px;
  padding: 10px 0;
  text-transform: none;
  
  &:hover {
    border-color: #E0E0E0;
    background-color: transparent;
  }
`;

const VipAdvantages = styled(Box)`
  background-color: #FBFAF8;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 40px;
  border: 1px solid #F0EBE4;
`;

const AdvantageTitle = styled(Typography)`
  font-weight: 600;
  font-size: 1.1rem;
  color: #2E353E;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
`;

const AdvantagesList = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
`;

const AdvantageItem = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AdvantageIcon = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #C9B18C;
  margin-left: 8px;
`;

const VipFooter = styled(Box)`
  text-align: center;
  margin-top: 30px;
  padding-top: 24px;
  border-top: 1px solid #F0EBE4;
`;
